import { FC, useRef, useState } from 'react';
import { Popover } from '@headlessui/react';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import { IService } from 'interfaces/services';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import CreateOrEditService, {
  CreateOrEditServiceSubmitData,
} from '../CreateOrEditService/CreateOrEditService';
import { loadSelectedClinicId } from 'utils/storage';
import axiosInstance from 'apis/axiosInstance';
import useMatchMutate from 'hooks/useMatchMutate';
import useOperatory from 'hooks/useOperatory';
import { renderToast } from 'components/Toast/Toast';
import { BAD_REQUEST } from 'utils/constants/statusCode';
import {
  convertDurationForBE,
  convertDurationForFE,
} from 'utils/convertDuration';
import { BOOKING_MODE, EXAM_PLACEMENT, SERVICE_NAME } from 'utils/constants';
import { isNumber } from 'lodash';

interface ThreeDotsOptionProps {
  service: IService;
  isSubRow: boolean;
  isParent: boolean;
  isHygieneChairSetup: boolean;
  timeInterval?: number;
  clinicServices: IService[];
  isDoctorSelectionEnabled: boolean;
  isHidden?: boolean;
  onSuccessfullyEdited?: () => void;
  isVisibilitySectionEnabled?: boolean;
  dropDownPositionClassName?: string;
  isClinicBlockSettingEnabled?: boolean;
}

const OPTIONS = {
  EDIT: { label: 'Edit', value: 'edit' },
  ACTIVATE: { label: 'Activate', value: 'activate' },
  DEACTIVATE: { label: 'Deactivate', value: 'deactivate' },
  DELETE: { label: 'Delete', value: 'delete' },
  VISIBILITY: { label: 'Change visibility', value: 'visibility' },
};

const { EDIT, ACTIVATE, DEACTIVATE, DELETE, VISIBILITY } = OPTIONS;

const ThreeDotsOption: FC<ThreeDotsOptionProps> = ({
  service,
  isSubRow,
  isParent,
  isHygieneChairSetup,
  timeInterval,
  clinicServices,
  isDoctorSelectionEnabled,
  isHidden = true,
  onSuccessfullyEdited,
  isVisibilitySectionEnabled,
  dropDownPositionClassName = 'right-0',
  isClinicBlockSettingEnabled,
}) => {
  const clinicId = loadSelectedClinicId();

  const url = `clinics/${clinicId}/services/${service.id}`;
  const [confirmModal, setConfirmModal] = useState<typeof EDIT | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const defaultValuesRef = useRef<any>(null);
  const { data: operatories } = useOperatory();

  // THE OPERATORY DROPDOWN WORKS WITH { label, value } OPTIONS WHILE THE
  // SERVICE ONLY KEEPS THE IGNORED OPERATORY IDS
  const getIgnoredOperatoryOptions = () =>
    (operatories ?? [])
      .filter((operatory) =>
        service.ignoredOperatoryIds?.includes(operatory.id)
      )
      .map((operatory) => ({ value: operatory.id, label: operatory.name }));

  const matchMutate = useMatchMutate();

  const getOptions = () => {
    const options = [EDIT];

    // if (!isParent) {
    //   options.push(EDIT);
    // }

    if (isSubRow) {
      return options;
    }

    const statusOption = service.isActive ? DEACTIVATE : ACTIVATE;

    options.push(statusOption);

    if (service.isCustom) {
      options.push(DELETE);
    }

    if (isVisibilitySectionEnabled) {
      options.push(VISIBILITY);
    }

    return options;
  };

  const handleClickOption = (option: typeof EDIT) => {
    setConfirmModal(option);
  };

  const onClose = () => {
    setConfirmModal(null);
    defaultValuesRef.current = null;
  };

  const checkDurationDuringFirstOrLastPart = (
    data: CreateOrEditServiceSubmitData
  ) => {
    let isDoctorDurationGreaterThanHygienistDuration = false;

    if (
      service.examPlacement === EXAM_PLACEMENT.DURING_FIRST_PART_DIFF ||
      service.examPlacement === EXAM_PLACEMENT.DURING_LAST_PART_DIFF
    ) {
      isDoctorDurationGreaterThanHygienistDuration =
        data.doctorDuration! > data.hygienistDuration!;
    }

    if (isDoctorDurationGreaterThanHygienistDuration) {
      renderToast({
        message:
          'Doctor duration should be less than or equal to hygienist duration for different chair mode.',
        type: 'error',
      });
    }
    return isDoctorDurationGreaterThanHygienistDuration;
  };

  const checkSplitSchedulingChange = (data: CreateOrEditServiceSubmitData) => {
    const isSplitSchedulingTurnedOff =
      !data.isSplitScheduling && service.isSplitScheduling;

    const examCleaningServices = clinicServices.find(
      (item) => item.name === SERVICE_NAME.EXAM_CLEANING
    );

    if (
      !isSubRow ||
      !isSplitSchedulingTurnedOff ||
      !examCleaningServices ||
      !isDoctorSelectionEnabled
    ) {
      return;
    }

    const otherChildServices = examCleaningServices.childServices.filter(
      (item) => item.id !== service.id
    );

    const isOtherChildServicesSplitSchedulingOff = otherChildServices.every(
      (item) => !item.isSplitScheduling
    );

    return (
      isOtherChildServicesSplitSchedulingOff &&
      isSplitSchedulingTurnedOff &&
      isDoctorSelectionEnabled
    );
  };

  const populatePayload = (data: CreateOrEditServiceSubmitData) => {
    const payload: any = {
      name: data.name,
      duration: convertDurationForBE(data.duration!),
      isActive: data.status === 'active',
      bookingMode: data.isChairScheduling
        ? BOOKING_MODE.CHAIR_SCHEDULING
        : BOOKING_MODE.DOCTOR_SCHEDULING,
      isBlockSchedulingEnabled: data.isBlockSchedulingEnabled,
      isPatientNoteRequired: data.isPatientNoteRequired,
      isDescriptionEnabled: data.isDescriptionEnabled,
    };

    // THE DESCRIPTION IS ONLY SENT WHILE ENABLED SO THE BACKEND KEEPS THE LAST
    // SAVED TEXT WHEN THE SETTING IS TURNED OFF
    if (data.isDescriptionEnabled) {
      payload.description = data.description;
    }

    if (data.displayName) {
      payload.displayName = data.displayName;
    }

    if (isSubRow) {
      payload.isSplitScheduling = data.isSplitScheduling;
    }

    if (isSubRow && data.isSplitScheduling) {
      payload.doctorDuration =
        data.doctorDuration !== null
          ? convertDurationForBE(data.doctorDuration!)
          : null;
      payload.hygienistDuration =
        data.hygienistDuration !== null
          ? convertDurationForBE(data.hygienistDuration!)
          : null;
      payload.doctorIds = data.doctorIds;
      payload.doctorChairs = data.doctorChairs;
    }

    const isAgeEditable = isNumber(data.fromAge) && isNumber(data.toAge);

    if (isAgeEditable) {
      payload.ages = [data.fromAge, data.toAge];
    }

    if (data.isBlockSchedulingEnabled) {
      payload.ignoreBlockSchedulingWithin = isNumber(
        data.ignoreBlockSchedulingWithin
      )
        ? data.ignoreBlockSchedulingWithin
        : null;

      payload.blockSchedulingStartAt = data.blockSchedulingStartAt;
    }

    if (isNumber(data.periodNewPatientYear)) {
      payload.periodNewPatientYear = data.periodNewPatientYear;
    }

    if (data.procedures) {
      payload.procedures = data.procedures;
    }

    // IGNORED CHAIRS ONLY APPLY TO PRACTITIONER SCHEDULING, SO TURNING CHAIR
    // SCHEDULING ON CLEARS THEM
    payload.ignoredOperatoryIds = data.isChairScheduling
      ? []
      : (data.ignoredOperatories ?? []).map((operatory) => operatory.value);

    return payload;
  };

  const sendEditRequest = async ({
    payload,
    successMessage,
  }: {
    payload: any;
    successMessage: string;
  }) => {
    try {
      await axiosInstance.patch(url, payload);
      renderToast({
        type: 'success',
        message: successMessage,
      });
      onSuccessfullyEdited?.();
      onClose();
    } catch (error: any) {
      if (error.response?.status === BAD_REQUEST) {
        return renderToast({
          message: error.response?.data.message,
          type: 'error',
        });
      }
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  const onEditService = async (data: CreateOrEditServiceSubmitData) => {
    const payload = populatePayload(data);

    const isDurationInvalid = checkDurationDuringFirstOrLastPart(data);
    const isSplitChangedNeedConfirm = checkSplitSchedulingChange(data);

    if (isDurationInvalid) return;

    if (isSplitChangedNeedConfirm) {
      onClose();
      defaultValuesRef.current = data;
      setIsModalOpen(true);
      return;
    }
    const successMessage = 'Updated a service successfully';

    await sendEditRequest({ payload, successMessage });
  };

  const onDeleteService = async () => {
    try {
      await axiosInstance.delete(url);

      renderToast({
        type: 'success',
        message: 'Deleted a service successfully',
      });
      onSuccessfullyEdited?.();
      onClose();
    } catch (error: any) {
      if (error.response?.status === BAD_REQUEST) {
        return renderToast({
          message: error.response?.data.message,
          type: 'error',
        });
      }

      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  const onEditServiceStatus = async () => {
    const payload = {
      isActive: !service.isActive,
    };
    const successMessage = `${confirmModal!.label} a service successfully`;

    await sendEditRequest({ payload, successMessage });
  };

  const onConfirmModel = async () => {
    const data = defaultValuesRef.current;
    if (!data) return;

    const payload = populatePayload(data);

    const successMessage = 'Updated a service successfully';

    // TURN OFF DOCTOR SELECTION
    try {
      await Promise.all([
        axiosInstance.patch(`clinics/${clinicId}/services/settings`, {
          isDoctorSelectionEnabled: false,
        }),
        axiosInstance.patch(url, payload),
      ]);

      defaultValuesRef.current = null;

      await matchMutate(/\/clinics\/[^/]+/);
      onSuccessfullyEdited?.();
      renderToast({
        type: 'success',
        message: successMessage,
      });
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  const onChangeVisibility = async () => {
    const payload = {
      isPublic: !service.isPublic,
    };
    const successMessage = `${confirmModal!.label} a service successfully`;

    await sendEditRequest({ payload, successMessage });
  };

  const onCloseModel = () => {
    setIsModalOpen(false);
    setConfirmModal(EDIT);
  };

  return (
    <>
      {isModalOpen && (
        <ConfirmationModal
          title={'Turn off split scheduling?'}
          description={
            'Disabling Split Scheduling will also prevent patients from selecting a doctor for exams/cleanings.'
          }
          onClose={onCloseModel}
          onSubmit={onConfirmModel}
          submitBtnTitle={'Accept'}
        />
      )}
      {confirmModal === EDIT && (
        <CreateOrEditService
          onClose={onClose}
          onSubmit={onEditService}
          defaultValues={
            defaultValuesRef.current ?? {
              name: service.name,
              duration: convertDurationForFE(service.duration),
              status: service.isActive ? 'active' : 'inactive',
              isChairScheduling:
                service.bookingMode === BOOKING_MODE.CHAIR_SCHEDULING,
              blockSchedulingStartAt: service.blockSchedulingStartAt,
              isBlockSchedulingEnabled: service.isBlockSchedulingEnabled,
              ignoreBlockSchedulingWithin: service.ignoreBlockSchedulingWithin,
              displayName: service.displayName,
              periodNewPatientYear: service.periodNewPatientYear,
              procedures: service.procedures,
              ignoredOperatories: getIgnoredOperatoryOptions(),
              isPatientNoteRequired: service.isPatientNoteRequired,
              isDescriptionEnabled: service.isDescriptionEnabled,
              description: service.description,
              ...(isSubRow && {
                doctorDuration: service.doctorDuration,
                hygienistDuration: service.hygienistDuration,
                isSplitScheduling: service.isSplitScheduling,
                examPlacement: service.examPlacement,
                fromAge: service.fromAge,
                toAge: service.toAge,
              }),
            }
          }
          isNameDisabled={!service.isCustom}
          isExamCleaningService={isSubRow}
          timeInterval={timeInterval}
          serviceId={service.id}
          shouldSkipRevalidate={Boolean(defaultValuesRef.current)}
          isClinicBlockSettingEnabled={isClinicBlockSettingEnabled}
        />
      )}
      {confirmModal === VISIBILITY && (
        <ConfirmationModal
          title={`Make service ${service.isPublic ? 'private' : 'public'}?`}
          description={`Are you sure you want to make ${service.name} ${service.isPublic ? 'private' : 'public'}`}
          onClose={onClose}
          onSubmit={onChangeVisibility}
          submitBtnTitle={`Yes, change`}
        />
      )}
      {confirmModal === DELETE && (
        <ConfirmationModal
          title="Delete service?"
          description={`Are you sure you want to delete ${service.name}`}
          onClose={onClose}
          onSubmit={onDeleteService}
          submitBtnTitle={`Yes, delete`}
        />
      )}
      {(confirmModal === ACTIVATE || confirmModal === DEACTIVATE) && (
        <ConfirmationModal
          title={`${confirmModal.label} service?`}
          description={`Are you sure you want to ${confirmModal.value} ${service.name}`}
          onClose={onClose}
          onSubmit={onEditServiceStatus}
          submitBtnTitle={`Yes, ${confirmModal.value}`}
        />
      )}
      <Popover
        className={`relative ${isHidden ? 'hidden' : ''} group-hover:block`}
      >
        <Popover.Button className="flex items-center disabled:cursor-not-allowed disabled:opacity-30">
          <MoreIcon className="h-2.2" />
        </Popover.Button>
        <Popover.Panel
          className={`flex flex-col gap-y-1.6 absolute py-2 z-10 bg-white shadow-primary rounded-[0.8rem] mt-0.6 w-16.5 ${dropDownPositionClassName}`}
        >
          {getOptions().map((option) => (
            <Popover.Button
              key={option.value}
              className="w-full px-2 hover:bg-opacity-10 hover:text-magenta text-left disabled:text-grey"
              onClick={() => handleClickOption(option)}
              disabled={isParent && !isHygieneChairSetup}
            >
              {option.label}
            </Popover.Button>
          ))}
        </Popover.Panel>
      </Popover>
    </>
  );
};

export default ThreeDotsOption;
